import apiClient from '@/shared/services/apiClient'
import type {
  ApiResponse,
  StartTestAttemptRequest,
  TestAttemptResponse,
  TestAttemptDetailResponse,
  UpdateQuestionResponseRequest,
  QuestionResponseResponse,
  QuestionResponseDetailResponse,
  RecordQuestionEventsRequest,
  QuestionResponse,
  AttemptAnalyticsResponse,
  TestAggregateStatsResponse,
  LeaderboardEntry,
  PagedResponse,
  StudentAttemptsParams,
  AttemptResponsesParams,
} from '../types'

class MockTestService {
  private readonly testAttemptsBaseUrl = '/api/v1/test-attempts'
  private readonly questionsBaseUrl = '/api/v1/question-bank/questions'

  // ============ Test Attempt Commands ============

  /**
   * Start a new test attempt
   */
  async startTestAttempt(
    request: StartTestAttemptRequest
  ): Promise<ApiResponse<TestAttemptResponse>> {
    const response = await apiClient.post<ApiResponse<TestAttemptResponse>>(
      `${this.testAttemptsBaseUrl}/`,
      request
    )
    return response.data
  }

  /**
   * Submit a test attempt
   */
  async submitTestAttempt(attemptId: string): Promise<ApiResponse<TestAttemptResponse>> {
    const response = await apiClient.post<ApiResponse<TestAttemptResponse>>(
      `${this.testAttemptsBaseUrl}/${attemptId}/submit`
    )
    return response.data
  }

  /**
   * Abandon a test attempt
   */
  async abandonTestAttempt(attemptId: string): Promise<ApiResponse<TestAttemptResponse>> {
    const response = await apiClient.post<ApiResponse<TestAttemptResponse>>(
      `${this.testAttemptsBaseUrl}/${attemptId}/abandon`
    )
    return response.data
  }

  /**
   * Update question response (save answer, mark for review, etc.)
   */
  async updateQuestionResponse(
    attemptId: string,
    request: UpdateQuestionResponseRequest
  ): Promise<ApiResponse<QuestionResponseResponse>> {
    const response = await apiClient.post<ApiResponse<QuestionResponseResponse>>(
      `${this.testAttemptsBaseUrl}/${attemptId}/responses`,
      request
    )
    return response.data
  }

  /**
   * Record question interaction events (for analytics)
   */
  async recordQuestionEvents(
    attemptId: string,
    request: RecordQuestionEventsRequest
  ): Promise<ApiResponse<void>> {
    const response = await apiClient.post<ApiResponse<void>>(
      `${this.testAttemptsBaseUrl}/${attemptId}/events`,
      request
    )
    return response.data
  }

  // ============ Test Attempt Queries ============

  /**
   * Get test attempt by ID
   */
  async getTestAttempt(attemptId: string): Promise<ApiResponse<TestAttemptDetailResponse>> {
    const response = await apiClient.get<ApiResponse<TestAttemptDetailResponse>>(
      `${this.testAttemptsBaseUrl}/${attemptId}`
    )
    return response.data
  }

  /**
   * Get all attempts by student for a specific test
   */
  async getStudentAttemptsForTest(
    studentId: string,
    testCode: string
  ): Promise<ApiResponse<TestAttemptDetailResponse[]>> {
    const response = await apiClient.get<ApiResponse<TestAttemptDetailResponse[]>>(
      `${this.testAttemptsBaseUrl}/student/${encodeURIComponent(studentId)}/test/${encodeURIComponent(testCode)}`
    )
    return response.data
  }

  /**
   * Get all attempts by student (paginated)
   */
  async getStudentAttempts(
    studentId: string,
    params?: StudentAttemptsParams
  ): Promise<PagedResponse<TestAttemptDetailResponse>> {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.page !== undefined) queryParams.append('page', params.page.toString())
    if (params?.size !== undefined) queryParams.append('size', params.size.toString())
    if (params?.sort) queryParams.append('sort', params.sort)

    const queryString = queryParams.toString()
    const url = `${this.testAttemptsBaseUrl}/student/${encodeURIComponent(studentId)}${queryString ? `?${queryString}` : ''}`

    const response = await apiClient.get<PagedResponse<TestAttemptDetailResponse>>(url)
    return response.data
  }

  /**
   * Get all question responses for an attempt (with optional filters)
   */
  async getAttemptResponses(
    attemptId: string,
    params?: AttemptResponsesParams
  ): Promise<ApiResponse<QuestionResponseDetailResponse[]>> {
    const queryParams = new URLSearchParams()
    if (params?.subjectCode) queryParams.append('subjectCode', params.subjectCode)
    if (params?.difficulty) queryParams.append('difficulty', params.difficulty)
    if (params?.isCorrect !== undefined) queryParams.append('isCorrect', params.isCorrect.toString())

    const queryString = queryParams.toString()
    const url = `${this.testAttemptsBaseUrl}/${attemptId}/responses${queryString ? `?${queryString}` : ''}`

    const response = await apiClient.get<ApiResponse<QuestionResponseDetailResponse[]>>(url)
    return response.data
  }

  /**
   * Get detailed analytics for a test attempt
   */
  async getAttemptAnalytics(attemptId: string): Promise<ApiResponse<AttemptAnalyticsResponse>> {
    const response = await apiClient.get<ApiResponse<AttemptAnalyticsResponse>>(
      `${this.testAttemptsBaseUrl}/${attemptId}/analytics`
    )
    return response.data
  }

  /**
   * Get aggregate statistics for a test
   */
  async getTestAggregateStats(testCode: string): Promise<ApiResponse<TestAggregateStatsResponse>> {
    const response = await apiClient.get<ApiResponse<TestAggregateStatsResponse>>(
      `${this.testAttemptsBaseUrl}/aggregate-stats/test/${encodeURIComponent(testCode)}`
    )
    return response.data
  }

  /**
   * Get leaderboard for a test
   */
  async getTestLeaderboard(
    testCode: string,
    page = 0,
    size = 50
  ): Promise<PagedResponse<LeaderboardEntry>> {
    const response = await apiClient.get<PagedResponse<LeaderboardEntry>>(
      `${this.testAttemptsBaseUrl}/leaderboard/test/${encodeURIComponent(testCode)}?page=${page}&size=${size}`
    )
    return response.data
  }

  // ============ Question Bank Queries ============

  /**
   * Get questions by IDs (fetches individually since batch API doesn't return options)
   */
  async getQuestionsByIds(questionIds: string[]): Promise<ApiResponse<{ questions: QuestionResponse[] }>> {
    if (questionIds.length === 0) {
      return {
        success: true,
        message: 'No questions to fetch',
        data: { questions: [] },
        timestamp: new Date().toISOString(),
      }
    }

    // Fetch all questions in parallel
    const questionPromises = questionIds.map((id) =>
      apiClient
        .get<ApiResponse<QuestionResponse>>(`${this.questionsBaseUrl}/${id}`)
        .then((res) => res.data.data)
        .catch((error) => {
          console.error(`Failed to fetch question ${id}:`, error)
          return null
        })
    )

    const results = await Promise.all(questionPromises)
    const questions = results.filter((q): q is QuestionResponse => q !== null)

    return {
      success: true,
      message: `Fetched ${questions.length} of ${questionIds.length} questions`,
      data: { questions },
      timestamp: new Date().toISOString(),
    }
  }

  /**
   * Get a single question by ID (for full details including solution)
   */
  async getQuestionById(questionId: string): Promise<ApiResponse<QuestionResponse>> {
    const response = await apiClient.get<ApiResponse<QuestionResponse>>(
      `${this.questionsBaseUrl}/${questionId}`
    )
    return response.data
  }
}

export default new MockTestService()
